import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ActiveChannels, Channels } from '../common/channelsEnum';
import { getChannelShortName } from '../../utils/readable';
import Card from 'antd/lib/card';
import Col from 'antd/lib/col';
import Row from 'antd/lib/row';
import {
  AccountType,
  DefaultUsage,
  EnvironmentSettingsItem,
  UsageItem
} from '../../api/environments/interface';
import { Button, Divider, Typography } from 'antd';
import Title from 'antd/es/typography/Title';
import Paragraph from 'antd/es/typography/Paragraph';
import { readWebsiteVariable } from '../../utils/readWebsiteVariable';

const Text = Typography.Text;

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const channelColors = {
  EMAIL: '#1890ff',
  INAPP_WEB: '#13c2c2',
  WEB_PUSH: '#473198',
  PUSH: '#F46036',
  CALL: '#52c41a',
  SMS: '#eb2f96'
};

interface Props {
  envSettingsItem: EnvironmentSettingsItem | undefined;
  usageItems: UsageItem[] | undefined;
}
const PlanPage = ({ envSettingsItem, usageItems }: Props): JSX.Element => {
  const [chartData, setChartData] = useState({
    labels: [''],
    datasets: [
      {
        label: '',
        data: [0]
      }
    ]
  });
  const [usageLimit, setUsageLimit] =
    useState<EnvironmentSettingsItem['usageLimit']>();
  const [currentUsage, setCurrentUsage] =
    useState<{
      EMAIL: number;
      INAPP_WEB: number;
      WEB_PUSH: number;
      PUSH: number;
      SMS: number;
      CALL: number;
    }>();

  const plans = [
    {
      title: 'Business - 200k',
      price: '$199',
      cap: '200,000',
      redirectURL: 'https://buy.stripe.com/14k4iU4iv75tbu07sA',
      dedicatedIP: true
    },
    {
      title: 'Business - 500k',
      price: '$449',
      cap: '500,000',
      redirectURL: 'https://buy.stripe.com/28o8za02fblJ1TqcMY',
      dedicatedIP: true
    },
    {
      title: 'Business - 1M',
      price: '$799',
      cap: '1,000,000',
      redirectURL: 'https://buy.stripe.com/3cseXy16j9dBeGccMX',
      dedicatedIP: true
    },
    {
      title: 'Business - 2M',
      price: '$1,199',
      cap: '2,000,000',
      redirectURL: 'https://buy.stripe.com/6oE4iU8yLblJ1Tq5kx',
      dedicatedIP: true
    },
    {
      title: 'Business - 3M',
      price: '$1,699',
      cap: '3,000,000',
      redirectURL: 'https://buy.stripe.com/00g02E8yL61pbu0fZc',
      dedicatedIP: true
    },
    {
      title: 'Business - 4M',
      price: '$2,099',
      cap: '4,000,000',
      redirectURL: 'https://buy.stripe.com/28o02E4ivcpNbu06oD',
      dedicatedIP: true
    },
    {
      title: 'Business - 5M',
      price: '$2,399',
      cap: '5,000,000',
      redirectURL: 'https://buy.stripe.com/aEU2aM6qDblJcy47sI',
      dedicatedIP: true
    }
  ];

  useEffect(() => {
    if (!envSettingsItem || !usageItems) return;
    setUsageLimit({ ...DefaultUsage, ...envSettingsItem.usageLimit });

    // set current usage
    const d = new Date();
    const currentYearMonth = `${d.getUTCFullYear()}${d.getMonth() + 1}`;
    const usage = {
      EMAIL: 0,
      INAPP_WEB: 0,
      WEB_PUSH: 0,
      PUSH: 0,
      SMS: 0,
      CALL: 0
    };

    Object.values(ActiveChannels).forEach((c) => {
      const valueFromDb = usageItems.find(
        (r) =>
          r.envIdChannel === `${envSettingsItem.envId}:${c}` &&
          r.yearMonth === currentYearMonth
      );
      usage[c] = valueFromDb ? (valueFromDb['channelUsage'] as number) : 0;
    });
    setCurrentUsage(usage);

    // chart stuff:
    const yearMonths: string[] = [];
    for (let index = 11; index >= 0; index--) {
      const d = new Date();
      d.setMonth(d.getMonth() - index);
      yearMonths.push(`${d.getUTCFullYear()}${d.getUTCMonth() + 1}`);
    }

    // create chart data for the usage values
    const datasets = Object.values(ActiveChannels).map((c, i) => {
      const values = yearMonths.map((yearMonth) => {
        const valueFromDb = usageItems.find(
          (r) =>
            r.envIdChannel === `${envSettingsItem.envId}:${c}` &&
            r.yearMonth === yearMonth
        );
        return valueFromDb ? (valueFromDb['channelUsage'] as number) : 0;
      });

      return {
        label: c,
        data: values
      };
    });

    const readableYearMonths: string[] = [];
    for (let index = 11; index >= 0; index--) {
      const d = new Date();
      d.setMonth(d.getMonth() - index);
      readableYearMonths.push(`${d.getUTCFullYear()}-${d.getUTCMonth() + 1}`);
    }

    setChartData({ labels: readableYearMonths, datasets });
  }, [envSettingsItem, usageItems]);

  return (
    <div style={{ padding: '24px' }}>
      {envSettingsItem && envSettingsItem.accountType === AccountType.FREE && (
        <>
          <Title level={3} style={{ marginTop: 0 }}>
            Plan
          </Title>
          <Row gutter={[20, 20]}>
            <>
              <Col xs={24} md={12} lg={10} xl={8}>
                <Card
                  title="Free Forever (Current)"
                  style={{
                    border: '1px solid #000',
                    height: '100%'
                  }}
                >
                  <Paragraph>
                    ✅ 10,000 notifications / month <br />
                    ✅ Includes all 3rd-party costs <br />
                    ✅ Includes 100 SMS & automated calls for testing <br />
                    ✅ Customize branding <br />✅ 60 day log retention
                  </Paragraph>
                </Card>
              </Col>

              {plans.map((plan) => (
                <Col xs={24} md={12} lg={10} xl={8}>
                  <Card
                    title={plan.title}
                    style={{
                      height: '100%',
                      position: 'relative',
                      paddingBottom: 40
                    }}
                  >
                    <Paragraph>
                      Everything in Free, plus... <br />✅ {plan.cap}{' '}
                      notifications / month <br />
                      {plan.dedicatedIP && (
                        <>
                          ✅ Dedicated IPs <br />
                        </>
                      )}
                    </Paragraph>

                    <div
                      style={{
                        position: 'absolute',
                        bottom: 24,
                        left: 24,
                        right: 24
                      }}
                    >
                      <Row
                        justify="space-between"
                        align="middle"
                        style={{
                          marginTop: 30
                        }}
                      >
                        <Text strong>{plan.price} / month</Text>
                        <Button href={plan.redirectURL} target="_blank">
                          Upgrade
                        </Button>
                      </Row>
                    </div>
                  </Card>
                </Col>
              ))}

              <Col xs={24} md={12} lg={10} xl={8}>
                <Card title="Enterprise">
                  <Paragraph>
                    ✅ Implementation <br />
                    ✅ Dedicated Support <br />
                    ✅ Deliverability Consultation <br />
                    ✅ Compliance (SLA, DPA, ...) <br />✅ RBAC, SSO
                  </Paragraph>
                  <Row justify="end">
                    <Button
                      href="https://www.notificationapi.com/contact"
                      target="_blank"
                    >
                      Contact
                    </Button>
                  </Row>
                </Card>
              </Col>
            </>
          </Row>
        </>
      )}

      {envSettingsItem && envSettingsItem.accountType === AccountType.PAID && (
        <Col xs={24} md={12} lg={10} xl={8}>
          <Button
            target="_blank"
            href={
              readWebsiteVariable('REACT_APP_domain') === 'ipanoitacifiton.com'
                ? 'https://billing.stripe.com/p/login/test_8wMdUB7KXcns6qcdQQ' // test stripe
                : 'https://billing.stripe.com/p/login/dR6cQc03J9qBcBG144' // production stripe
            }
          >
            Manage Plan & Billing
          </Button>
        </Col>
      )}

      <Divider />
      <Title level={3}>Monthly Usage</Title>

      <Row>
        {!usageLimit?.EMAIL && (
          <Col xs={24} md={12} lg={10} xl={8}>
            <Card
              loading={envSettingsItem === undefined}
              title="Email, In-App, Mobile, Web Push"
              style={{ marginRight: '20px', marginBottom: '20px' }}
            >
              <Text strong>
                {Object.values(ActiveChannels).reduce((pre, channel) => {
                  if (
                    channel !== ActiveChannels.SMS &&
                    channel !== ActiveChannels.CALL
                  )
                    return pre + (currentUsage?.[channel] || 0);
                  else return pre;
                }, 0)}
              </Text>
              <Text type="secondary">/{usageLimit?.GENERAL}</Text>
            </Card>
          </Col>
        )}
        {Object.values(ActiveChannels)
          .filter((c) => {
            return usageLimit?.[c];
          })
          .map((c) => {
            return (
              <Col xs={24} md={4} lg={6} xl={4} key={c}>
                <Card
                  loading={envSettingsItem === undefined}
                  title={getChannelShortName(c)}
                  style={{ marginRight: '20px', marginBottom: '20px' }}
                >
                  <Text strong>{currentUsage ? currentUsage[c] : 0}</Text>
                  <Text type="secondary">
                    /{usageLimit ? usageLimit[c] : ''}
                  </Text>
                </Card>
              </Col>
            );
          })}
      </Row>
      <Card>
        <div style={{ height: '50vh' }}>
          <Line
            options={{
              layout: {
                padding: 10
              },
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                tooltip: {
                  padding: 20,
                  titleMarginBottom: 10,
                  boxPadding: 10,
                  boxHeight: 10,
                  boxWidth: 40,
                  backgroundColor: 'rgba(0,0,0,0.7)'
                }
              },
              interaction: {
                mode: 'nearest',
                axis: 'x',
                intersect: false
              },
              scales: {
                y: {
                  beginAtZero: true,
                  suggestedMax: 10000
                }
              }
            }}
            data={{
              labels: chartData.labels,
              datasets: chartData.datasets.map((ds) => {
                return {
                  label: getChannelShortName(ds.label),
                  data: ds.data,
                  fill: true,
                  backgroundColor: channelColors[ds.label as Channels],
                  tension: 0.33, // curvy smooth graph
                  borderWidth: 0,
                  pointRadius: 0,
                  stack: 'usage'
                };
              })
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default PlanPage;
